import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import type { AgentTaskModel } from "@fencyai/js";
import { FencyProvider } from "@fencyai/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fencyPromise, FENCY_DEFAULT_MODEL } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { useStructuredCompletion } from "../fency/useStructuredCompletion";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";

export type AiMessage = { role: "SYSTEM" | "USER" | "ASSISTANT"; content: string };

export type AiDraftModalProps = {
  /** Modal header — caller decides what is being drafted (e.g. "Suggest description with AI"). */
  title: string;
  /** RUM tag for failures, e.g. "se-public-description". */
  feature: string;
  /** Domain prompt; called on each generate so it can reflect the latest field state. */
  buildMessages: () => AiMessage[];
  /** Receives the accepted draft as TipTap JSON, ready for editor.commands.setContent. */
  onInsert: (tiptapJson: string) => void;
  onClose: () => void;
  model?: AgentTaskModel;
};

const DRAFT_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    text: { type: "string", description: "The generated rich text, formatted as Markdown." },
  },
  required: ["text"],
});

export const AiDraftModal = (props: AiDraftModalProps) => {
  const { fetchCreateStreamClientToken, fetchCreateStructuredChatCompletionClientToken } = useFency();
  return (
    <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
      <AiDraftModalInner {...props} fetchClientToken={fetchCreateStructuredChatCompletionClientToken} />
    </FencyProvider>
  );
};

type InnerProps = AiDraftModalProps & {
  fetchClientToken: () => Promise<{ clientToken: string }>;
};

const AiDraftModalInner = ({ title, feature, buildMessages, onInsert, onClose, model, fetchClientToken }: InnerProps) => {
  const { t } = useTranslation();
  const runStructured = useStructuredCompletion({ feature, fetchClientToken });

  const [draft, setDraft] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const draftJson = useMemo(() => (draft ? getTiptapJsonFromMarkdown(draft) : null), [draft]);

  const generate = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setDraft(null);
    try {
      const json = await runStructured({ model: model ?? FENCY_DEFAULT_MODEL, jsonSchema: DRAFT_SCHEMA, messages: buildMessages() });
      const text = parseText(json);
      if (text) setDraft(text);
      else setHasError(true);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [runStructured, model, buildMessages]);

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void generate();
  }, [generate]);

  return (
    <Modal isOpen onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading && (
            <Flex align="center" gap="3" py="8" color="smMuted">
              <Spinner size="sm" />
              <Text fontSize="sm">{t("Generating a suggestion…")}</Text>
            </Flex>
          )}
          {hasError && !isLoading && (
            <Text color="smError" fontSize="sm" py="4">
              {t("Couldn't generate a suggestion. Please try again.")}
            </Text>
          )}
          {draftJson && !isLoading && <TipTap content={draftJson} editable={false} maxHeight="96" />}
        </ModalBody>
        <ModalFooter gap="2">
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="outline" colorScheme="teal" onClick={() => void generate()} isLoading={isLoading}>
            {t("Regenerate")}
          </Button>
          <Button
            colorScheme="teal"
            isDisabled={!draftJson || isLoading}
            onClick={() => draftJson && onInsert(draftJson)}
          >
            {t("Insert")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const parseText = (json: string): string => {
  try {
    const data = JSON.parse(json) as { text?: unknown };
    return typeof data.text === "string" ? data.text.trim() : "";
  } catch {
    return "";
  }
};
